import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AdherenceMetricsChart = ({ clients, calculateClientStats }) => {
  const [chartType, setChartType] = useState('bar'); // bar or line
  const [sortBy, setSortBy] = useState('adherence'); // adherence or completion

  // Prepare chart data
  const chartData = clients
    ?.map(client => {
      const stats = calculateClientStats(client?.id);
      return {
        name: client?.fullName?.split(' ')?.[0] || 'Client',
        adherence: stats?.adherenceScore,
        completion: stats?.completionRate,
        total: stats?.totalWorkouts
      };
    })
    ?.sort((a, b) => {
      if (sortBy === 'adherence') {
        return b?.adherence - a?.adherence;
      }
      return b?.completion - a?.completion;
    })
    ?.slice(0, 10); // Show top 10 clients

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{payload?.[0]?.payload?.name}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-muted-foreground">Adherence:</span>
              <span className="font-semibold text-foreground">{payload?.[0]?.payload?.adherence}%</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-muted-foreground">Completion:</span>
              <span className="font-semibold text-foreground">{payload?.[0]?.payload?.completion}%</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Total Workouts:</span>
              <span className="font-semibold text-foreground">{payload?.[0]?.payload?.total}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const averageAdherence = chartData?.length > 0
    ? Math.round(chartData?.reduce((sum, d) => sum + d?.adherence, 0) / chartData?.length)
    : 0;

  const averageCompletion = chartData?.length > 0
    ? Math.round(chartData?.reduce((sum, d) => sum + d?.completion, 0) / chartData?.length)
    : 0;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Adherence Metrics</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Client engagement patterns and consistency scores
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={sortBy === 'adherence' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('adherence')}
          >
            Sort by Adherence
          </Button>
          <Button
            variant={sortBy === 'completion' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('completion')}
          >
            Sort by Completion
          </Button>
          <div className="border-l border-border mx-1"></div>
          <Button
            variant={chartType === 'bar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('bar')}
          >
            <Icon name="BarChart3" size={16} />
          </Button>
          <Button
            variant={chartType === 'line' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('line')}
          >
            <Icon name="TrendingUp" size={16} />
          </Button>
        </div>
      </div>

      {/* Average Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={20} className="text-blue-500" />
            <span className="text-sm text-muted-foreground">Avg Adherence</span>
          </div>
          <p className="text-3xl font-bold text-blue-500">{averageAdherence}%</p>
        </div>
        <div className="bg-green-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" size={20} className="text-green-500" />
            <span className="text-sm text-muted-foreground">Avg Completion</span>
          </div>
          <p className="text-3xl font-bold text-green-500">{averageCompletion}%</p>
        </div>
      </div>

      {/* Chart */}
      {chartData?.length > 0 ? (
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="name" 
                  stroke="var(--color-muted-foreground)" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)" 
                  fontSize={12}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="adherence" 
                  fill="#3B82F6" 
                  radius={[4, 4, 0, 0]}
                  name="Adherence"
                />
                <Bar 
                  dataKey="completion" 
                  fill="#10B981" 
                  radius={[4, 4, 0, 0]}
                  name="Completion"
                />
              </BarChart>
            ) : (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="name" 
                  stroke="var(--color-muted-foreground)" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)" 
                  fontSize={12}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="adherence" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  name="Adherence"
                />
                <Line 
                  type="monotone" 
                  dataKey="completion" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  name="Completion"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-20">
          <Icon name="BarChart3" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No client data available for metrics</p>
        </div>
      )}

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500"></div>
          <span className="text-sm text-muted-foreground">Adherence Score (30 days)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-sm text-muted-foreground">Completion Rate (all time)</span>
        </div>
      </div>
    </div>
  );
};

export default AdherenceMetricsChart;